import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../ui/dialog";
import { CheckCircle2, DollarSign, CalendarDays, CalendarCheck, Users, Percent, UserPlus, Trash2, User, Edit2, AlertTriangle, Lock } from "lucide-react";
import { saleDateWarnings } from "../../features/properties/utils/saleDetails";

// A blank purchaser row.
const emptyPurchaser = () => ({ name: "", email: "", contact: "" });

// Stable default for initialPurchasers. A fresh `[]` default on every render
// changes the reset effect's dependency each time, so the effect re-runs,
// re-seeds the form and re-renders forever (the Collings page never passes
// purchasers, so its dialog kept resetting itself).
const NO_PURCHASERS = [];

/**
 * Modal shown when an agent clicks "Mark as Sold", and again (mode="edit") from
 * "Edit Sale Details" on a property that is already sold, so a wrong sold date,
 * price or split can be corrected without marking it unsold first.
 *
 * Captures:
 *  - the price the property actually sold for
 *  - the sold date — shown online as the "Sold on" date and used as a
 *    leaderboard dating fallback when no settlement date is set. Starts on the
 *    recorded sold date, else `defaultSoldDate` (the contract date when the
 *    page knows one), else today
 *  - an optional "suppress from portals" flag
 *  - the settlement date (drives the commission's financial-year bucket on the
 *    Commissions leaderboard)
 *  - an optional agent commission split: a co-agent (the listing's own co-agent
 *    or anyone in the office) plus the primary agent's split percentage (the
 *    remainder goes to the co-agent). A one-tap "Split 50/50" sets an even split.
 *
 * When suppressed, the sold price is hidden on public portals and the listing
 * shows "Contact Agent" instead.
 *
 * onConfirm receives:
 *   {
 *     soldPrice: number,
 *     soldDate: string ("YYYY-MM-DD" — defaults to today),
 *     suppressSoldPrice: boolean,
 *     settlementDate: string ("YYYY-MM-DD" or ""),
 *     coAgentId: string ("" when none),
 *     splitPercentage: number (primary agent's % — only meaningful with a co-agent),
 *     purchasers: Array<{ name, email, contact }> (the buyer(s); blank rows dropped),
 *   }
 *
 * Portal attribution is set separately on the property page via the DOMA
 * category picker (picking a category tags the sale), so it's not captured here.
 */
const MarkSoldModal = ({
    isOpen,
    onClose,
    onConfirm,
    loading = false,
    address = "",
    initialSoldPrice = "",
    initialSuppress = false,
    agents = [],
    primaryAgentName = "",
    primaryAgentId = "",
    listingCoAgent = null,
    initialSoldDate = "",
    initialSettlementDate = "",
    initialCoAgentId = "",
    initialSplitPercentage = "",
    initialNoCommissionCredit = false,
    initialPurchasers = NO_PURCHASERS,
    // Sold date to start on when the property has none recorded yet (the
    // contract date). Blank means the agent must pick one.
    defaultSoldDate = "",
    // The property's contract (sale-agreed) date, for the date sanity checks.
    contractDate = "",
    // realestate.com.au already has this sale and won't accept changes to it.
    reaSoldLocked = false,
    // "mark" (default) marks the property sold; "edit" corrects an existing sale.
    mode = "mark",
}) => {
    const isEdit = mode === "edit";
    const [soldPrice, setSoldPrice] = useState("");
    const [soldDate, setSoldDate] = useState("");
    const [suppressSoldPrice, setSuppressSoldPrice] = useState(false);
    const [settlementDate, setSettlementDate] = useState("");
    const [coAgentId, setCoAgentId] = useState("");
    const [splitPercentage, setSplitPercentage] = useState("50");
    // A no-commission-credit sale (0% to both agents): recorded, but excluded
    // from both agents' GCI and leaderboard. When on, the split inputs are
    // hidden — the whole point is that neither agent is credited.
    const [noCommissionCredit, setNoCommissionCredit] = useState(false);
    const [purchasers, setPurchasers] = useState([emptyPurchaser()]);
    const [error, setError] = useState("");

    // Reset the form each time the modal is opened
    useEffect(() => {
        if (isOpen) {
            setSoldPrice(
                initialSoldPrice !== null && initialSoldPrice !== undefined
                    ? String(initialSoldPrice)
                    : "",
            );
            // Recorded sold date, else the contract date when known, else
            // blank so the agent picks it on purpose.
            setSoldDate(initialSoldDate || defaultSoldDate || "");
            setSuppressSoldPrice(Boolean(initialSuppress));
            setSettlementDate(initialSettlementDate || "");
            setCoAgentId(initialCoAgentId ? String(initialCoAgentId) : "");
            setSplitPercentage(
                initialSplitPercentage !== null &&
                    initialSplitPercentage !== undefined &&
                    initialSplitPercentage !== ""
                    ? String(initialSplitPercentage)
                    : "50",
            );
            setNoCommissionCredit(Boolean(initialNoCommissionCredit));
            // Seed the purchaser rows from any known buyers; always leave at least
            // one (blank) row so the section is ready to fill in.
            const seeded = (initialPurchasers || [])
                .map((p) => ({
                    name: p.name || "",
                    email: p.email || "",
                    contact: p.contact || p.phone || "",
                }))
                .filter((p) => p.name || p.email || p.contact);
            setPurchasers(seeded.length ? seeded : [emptyPurchaser()]);
            setError("");
        }
    }, [
        isOpen,
        initialSoldPrice,
        initialSoldDate,
        defaultSoldDate,
        initialSuppress,
        initialSettlementDate,
        initialCoAgentId,
        initialSplitPercentage,
        initialNoCommissionCredit,
        initialPurchasers,
    ]);

    // Keep only digits and a single decimal point as the user types
    const handlePriceChange = (e) => {
        const cleaned = e.target.value.replace(/[^0-9.]/g, "");
        const parts = cleaned.split(".");
        const normalized =
            parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : cleaned;
        setSoldPrice(normalized);
        if (error) setError("");
    };

    // Group the integer part with thousands separators for display
    const formattedPrice = (() => {
        if (!soldPrice) return "";
        const [intPart, decPart] = soldPrice.split(".");
        const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        return decPart !== undefined ? `${grouped}.${decPart}` : grouped;
    })();

    // Only digits 0–100 for the split percentage
    const handleSplitChange = (e) => {
        const cleaned = e.target.value.replace(/[^0-9]/g, "").slice(0, 3);
        if (cleaned === "") {
            setSplitPercentage("");
        } else {
            setSplitPercentage(String(Math.min(100, parseInt(cleaned, 10))));
        }
        if (error) setError("");
    };

    // Dates that look wrong get a warning before they reach the portals
    // (realestate.com.au won't accept a corrected sold date later).
    const dateWarnings = saleDateWarnings({
        soldDate,
        settlementDate,
        contractDate: contractDate ? String(contractDate).slice(0, 10) : "",
        isEdit,
    });

    const primarySplitNum = parseInt(splitPercentage, 10);
    const coAgentSplit =
        coAgentId && !isNaN(primarySplitNum) ? 100 - primarySplitNum : null;

    // Mirror of handleSplitChange, but editing the CO-AGENT's own field — keeps
    // the pair summing to 100 by writing the complement into splitPercentage.
    // Lets either agent be set all the way down to 0% (e.g. one agent was
    // overseas and did nothing on the deal) or up to 100%.
    const handleCoAgentSplitChange = (e) => {
        const cleaned = e.target.value.replace(/[^0-9]/g, "").slice(0, 3);
        if (cleaned === "") {
            setSplitPercentage("");
        } else {
            const coVal = Math.min(100, parseInt(cleaned, 10));
            setSplitPercentage(String(100 - coVal));
        }
        if (error) setError("");
    };

    // Co-agent picker: the listing's own co-agent (if any) surfaces first and is
    // flagged, then everyone else in the office. The primary agent is excluded —
    // you can't split with yourself.
    const listingCoAgentId = listingCoAgent?.id
        ? String(listingCoAgent.id)
        : "";
    const coAgentOptions = (() => {
        const opts = (agents || [])
            .filter((a) => String(a.id) !== String(primaryAgentId))
            .map((a) => ({
                id: String(a.id),
                label: a.name || a.email || `Agent ${a.id}`,
                isListingCoAgent:
                    !!listingCoAgentId && String(a.id) === listingCoAgentId,
            }));
        // The listing's co-agent may not be in the office `agents` list — make
        // sure it's always selectable.
        if (
            listingCoAgentId &&
            listingCoAgentId !== String(primaryAgentId) &&
            !opts.some((o) => o.id === listingCoAgentId)
        ) {
            opts.unshift({
                id: listingCoAgentId,
                label:
                    listingCoAgent.name ||
                    listingCoAgent.email ||
                    `Agent ${listingCoAgentId}`,
                isListingCoAgent: true,
            });
        }
        // Listing co-agent(s) float to the top.
        opts.sort(
            (a, b) =>
                (b.isListingCoAgent ? 1 : 0) - (a.isListingCoAgent ? 1 : 0),
        );
        return opts;
    })();

    const coAgentName = coAgentId
        ? coAgentOptions.find((a) => a.id === coAgentId)?.label || ""
        : "";

    // One-tap even split. Picking a co-agent first is a no-op selection here, so
    // if none is chosen yet default to the listing co-agent when we have one.
    const applyEvenSplit = () => {
        if (!coAgentId && listingCoAgentId) {
            setCoAgentId(listingCoAgentId);
        }
        setSplitPercentage("50");
        if (error) setError("");
    };

    // ----- Purchaser rows -----
    const updatePurchaser = (index, field, value) => {
        setPurchasers((rows) =>
            rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
        );
        if (error) setError("");
    };

    const addPurchaser = () => {
        setPurchasers((rows) => [...rows, emptyPurchaser()]);
    };

    const removePurchaser = (index) => {
        setPurchasers((rows) => {
            const next = rows.filter((_, i) => i !== index);
            return next.length ? next : [emptyPurchaser()];
        });
    };

    const handleConfirm = () => {
        const value = parseFloat(soldPrice);
        if (!soldPrice || isNaN(value) || value <= 0) {
            setError("Enter the price the property sold for.");
            return;
        }
        if (!soldDate) {
            setError("Pick the date the property sold.");
            return;
        }
        // Drop fully-blank rows; a row needs at least a name or an email to count.
        const cleanedPurchasers = purchasers
            .map((p) => ({
                name: (p.name || "").trim(),
                email: (p.email || "").trim(),
                contact: (p.contact || "").trim(),
            }))
            .filter((p) => p.name || p.email);
        // A no-credit sale skips the split entirely — neither agent is credited,
        // so the split inputs are hidden and not validated.
        if (coAgentId && !noCommissionCredit) {
            // 0% and 100% are valid — e.g. a co-agent who was overseas and did
            // nothing on the deal gets 0%, the other agent gets full credit.
            if (isNaN(primarySplitNum) || primarySplitNum < 0 || primarySplitNum > 100) {
                setError("Split must be between 0 and 100%.");
                return;
            }
        }
        onConfirm({
            soldPrice: value,
            soldDate,
            suppressSoldPrice,
            settlementDate: settlementDate || "",
            coAgentId: coAgentId || "",
            splitPercentage: coAgentId ? primarySplitNum : 100,
            noCommissionCredit,
            purchasers: cleanedPurchasers,
        });
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[460px] rounded-2xl border-none shadow-2xl max-h-[90vh] grid-rows-[auto_1fr_auto] overflow-hidden">
                <DialogHeader>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-2xl bg-green-100 text-green-600">
                            {isEdit ? <Edit2 className="w-6 h-6" /> : <CheckCircle2 className="w-6 h-6" />}
                        </div>
                        <div>
                            <DialogTitle className="text-xl font-bold text-gray-900 leading-tight">
                                {isEdit ? "Edit Sale Details" : "Mark Property as Sold"}
                            </DialogTitle>
                            {address ? (
                                <p className="text-xs font-medium text-gray-400 mt-1 truncate max-w-[280px]">
                                    {address}
                                </p>
                            ) : null}
                        </div>
                    </div>
                </DialogHeader>

                <div className="py-2 space-y-4 overflow-y-auto min-h-0 -mr-2 pr-2">
                    {isEdit && reaSoldLocked ? (
                        <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[12px] font-medium text-amber-800">
                            <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>
                                realestate.com.au already has this sale and won&rsquo;t accept changes to it.
                                Changes here update the CRM, the Collings portal and the website. To change
                                realestate.com.au, contact REA support.
                            </span>
                        </div>
                    ) : null}
                    {/* Sale price */}
                    <div>
                        <label
                            htmlFor="mark-sold-price"
                            className="block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5"
                        >
                            Sale Price <span className="text-red-500">*</span>
                        </label>
                        <div
                            className={`flex items-center rounded-xl border bg-white transition-all focus-within:ring-2 ${
                                error
                                    ? "border-red-300 focus-within:ring-red-200"
                                    : "border-gray-200 focus-within:ring-[#55C5D0]/40"
                            }`}
                        >
                            <span className="flex items-center justify-center w-10 h-11 text-gray-400">
                                <DollarSign className="w-4 h-4" />
                            </span>
                            <input
                                id="mark-sold-price"
                                type="text"
                                inputMode="decimal"
                                autoFocus
                                value={formattedPrice}
                                onChange={handlePriceChange}
                                placeholder="0.00"
                                disabled={loading}
                                className="flex-1 h-11 pr-3 bg-transparent text-sm font-semibold text-gray-900 placeholder:text-gray-300 focus:outline-none"
                            />
                        </div>
                    </div>

                    {/* Sold date — shown online as the "Sold on" date + leaderboard dating fallback */}
                    <div>
                        <label
                            htmlFor="mark-sold-date"
                            className="block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5"
                        >
                            Sold Date <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center rounded-xl border border-gray-200 bg-white transition-all focus-within:ring-2 focus-within:ring-[#55C5D0]/40">
                            <span className="flex items-center justify-center w-10 h-11 text-gray-400">
                                <CalendarCheck className="w-4 h-4" />
                            </span>
                            <input
                                id="mark-sold-date"
                                type="date"
                                value={soldDate}
                                onChange={(e) => {
                                    setSoldDate(e.target.value);
                                    if (error) setError("");
                                }}
                                disabled={loading}
                                className="flex-1 h-11 pr-3 bg-transparent text-sm font-semibold text-gray-900 focus:outline-none"
                            />
                        </div>
                        <p className="text-[11px] font-medium text-gray-400 mt-1">
                            The date the sale was agreed &mdash; shown online and used to track the sale on the leaderboard. Starts on the contract date when one is recorded.
                        </p>
                    </div>

                    {/* Settlement date — drives the commission's financial year on the leaderboard */}
                    <div>
                        <label
                            htmlFor="mark-sold-settlement"
                            className="block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5"
                        >
                            Settlement Date
                        </label>
                        <div className="flex items-center rounded-xl border border-gray-200 bg-white transition-all focus-within:ring-2 focus-within:ring-[#55C5D0]/40">
                            <span className="flex items-center justify-center w-10 h-11 text-gray-400">
                                <CalendarDays className="w-4 h-4" />
                            </span>
                            <input
                                id="mark-sold-settlement"
                                type="date"
                                value={settlementDate}
                                onChange={(e) => setSettlementDate(e.target.value)}
                                disabled={loading}
                                className="flex-1 h-11 pr-3 bg-transparent text-sm font-semibold text-gray-900 focus:outline-none"
                            />
                        </div>
                        <p className="text-[11px] font-medium text-gray-400 mt-1">
                            Optional &mdash; when the sale settles. Dates the commission&rsquo;s financial year on the leaderboard; falls back to the sold date if left blank.
                        </p>
                    </div>

                    {dateWarnings.length ? (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-1">
                            {dateWarnings.map((w) => (
                                <p key={w} className="flex gap-2 text-[12px] font-medium text-amber-800">
                                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                    {w}
                                </p>
                            ))}
                        </div>
                    ) : null}

                    {/* Agent commission split */}
                    <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 space-y-3">
                        <div className="flex items-center gap-2 text-gray-700">
                            <Users className="w-4 h-4 text-[#55C5D0]" />
                            <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
                                Agent Commission Split
                            </span>
                        </div>

                        {/* No commission credit: a 0%/0% sale that is recorded
                            but excluded from both agents' GCI and leaderboard.
                            When on, the split inputs are hidden — neither agent
                            is credited, so there is nothing to split. */}
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={noCommissionCredit}
                                onChange={(e) => setNoCommissionCredit(e.target.checked)}
                                disabled={loading}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0]/40"
                            />
                            <span className="text-[12px] font-semibold text-gray-600 leading-snug">
                                No commission credit for this sale
                                <span className="block text-[11px] font-normal text-gray-400">
                                    Records the sale but excludes it from both agents' commission, GCI and leaderboard.
                                </span>
                            </span>
                        </label>

                        {!noCommissionCredit && (
                        <>
                        <div>
                            <label
                                htmlFor="mark-sold-co-agent"
                                className="block text-[11px] font-semibold text-gray-500 mb-1"
                            >
                                Co-Agent (optional)
                            </label>
                            <select
                                id="mark-sold-co-agent"
                                value={coAgentId}
                                onChange={(e) => setCoAgentId(e.target.value)}
                                disabled={loading}
                                className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/40"
                            >
                                <option value="">No co-agent (100% to primary)</option>
                                {coAgentOptions.map((agent) => (
                                    <option key={agent.id} value={agent.id}>
                                        {agent.label}
                                        {agent.isListingCoAgent
                                            ? " (co-agent on this listing)"
                                            : ""}
                                    </option>
                                ))}
                            </select>
                            <p className="text-[11px] font-medium text-gray-400 mt-1">
                                Split with the co-agent on this listing or anyone in the office.
                            </p>
                        </div>

                        {/* One-tap even split */}
                        <button
                            type="button"
                            onClick={applyEvenSplit}
                            disabled={loading}
                            className={`w-full flex items-center justify-center gap-2 h-10 rounded-xl border text-xs font-bold uppercase tracking-wide transition-all ${
                                coAgentId && String(splitPercentage) === "50"
                                    ? "border-[#55C5D0] bg-[#55C5D0]/10 text-[#3E9BA4]"
                                    : "border-gray-200 bg-white text-gray-600 hover:border-[#55C5D0]/60 hover:text-[#3E9BA4]"
                            }`}
                        >
                            <Percent className="w-3.5 h-3.5" />
                            Split 50/50
                        </button>

                        {coAgentId ? (
                            <div>
                                <span className="block text-[11px] font-semibold text-gray-500 mb-1">
                                    Commission Split
                                </span>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <label
                                            htmlFor="mark-sold-split"
                                            className="flex-1 text-sm font-medium text-gray-700 truncate"
                                        >
                                            {primaryAgentName || "Primary agent"}
                                        </label>
                                        <div className="flex items-center rounded-xl border border-gray-200 bg-white focus-within:ring-2 focus-within:ring-[#55C5D0]/40 w-24 shrink-0">
                                            <input
                                                id="mark-sold-split"
                                                type="text"
                                                inputMode="numeric"
                                                value={splitPercentage}
                                                onChange={handleSplitChange}
                                                placeholder="50"
                                                disabled={loading}
                                                className="flex-1 w-0 h-11 px-3 bg-transparent text-sm font-semibold text-gray-900 placeholder:text-gray-300 focus:outline-none text-right"
                                            />
                                            <span className="flex items-center justify-center w-8 h-11 text-gray-400 font-semibold">
                                                %
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <label
                                            htmlFor="mark-sold-co-agent-split"
                                            className="flex-1 text-sm font-medium text-gray-700 truncate"
                                        >
                                            {coAgentName || "Co-agent"}
                                        </label>
                                        <div className="flex items-center rounded-xl border border-gray-200 bg-white focus-within:ring-2 focus-within:ring-[#55C5D0]/40 w-24 shrink-0">
                                            <input
                                                id="mark-sold-co-agent-split"
                                                type="text"
                                                inputMode="numeric"
                                                value={coAgentSplit !== null ? String(coAgentSplit) : ""}
                                                onChange={handleCoAgentSplitChange}
                                                placeholder="50"
                                                disabled={loading}
                                                className="flex-1 w-0 h-11 px-3 bg-transparent text-sm font-semibold text-gray-900 placeholder:text-gray-300 focus:outline-none text-right"
                                            />
                                            <span className="flex items-center justify-center w-8 h-11 text-gray-400 font-semibold">
                                                %
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <p className="text-[11px] font-medium text-gray-400 mt-1">
                                    Splits add up to 100% — editing one updates the other. Set either agent to 0% for a full-credit sale.
                                </p>
                            </div>
                        ) : null}
                        </>
                        )}
                    </div>

                    {/* Suppress from portals */}
                    <label
                        htmlFor="mark-sold-suppress"
                        className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100 cursor-pointer select-none"
                    >
                        <input
                            id="mark-sold-suppress"
                            type="checkbox"
                            checked={suppressSoldPrice}
                            onChange={(e) => setSuppressSoldPrice(e.target.checked)}
                            disabled={loading}
                            className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0] cursor-pointer"
                        />
                        <span>
                            <span className="block text-sm font-semibold text-gray-800">
                                Suppress Sale Price from Portals
                            </span>
                            <span className="block text-xs font-medium text-gray-400 mt-0.5">
                                The price stays hidden online &mdash; the listing shows
                                &ldquo;Contact Agent&rdquo; instead.
                            </span>
                        </span>
                    </label>

                    {/* Purchaser details — who bought the property. Most sales aren't
                        negotiated through the portal, so capture the buyer(s) here.
                        A sale can have more than one purchaser (e.g. a couple). */}
                    <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 space-y-3">
                        <div className="flex items-center gap-2 text-gray-700">
                            <User className="w-4 h-4 text-[#55C5D0]" />
                            <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
                                Purchaser Details
                            </span>
                        </div>

                        {purchasers.map((p, index) => (
                            <div
                                key={index}
                                className="rounded-xl border border-gray-200 bg-white p-3 space-y-2"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
                                        {purchasers.length > 1
                                            ? `Purchaser ${index + 1}`
                                            : "Purchaser"}
                                    </span>
                                    {purchasers.length > 1 ? (
                                        <button
                                            type="button"
                                            onClick={() => removePurchaser(index)}
                                            disabled={loading}
                                            className="p-1 text-gray-400 hover:text-red-500 rounded-lg transition-colors"
                                            title="Remove purchaser"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    ) : null}
                                </div>
                                <input
                                    type="text"
                                    value={p.name}
                                    onChange={(e) =>
                                        updatePurchaser(index, "name", e.target.value)
                                    }
                                    placeholder="Full name"
                                    disabled={loading}
                                    className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-900 placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/40"
                                />
                                <div className="flex gap-2">
                                    <input
                                        type="email"
                                        value={p.email}
                                        onChange={(e) =>
                                            updatePurchaser(index, "email", e.target.value)
                                        }
                                        placeholder="Email"
                                        disabled={loading}
                                        className="flex-1 min-w-0 h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-900 placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/40"
                                    />
                                    <input
                                        type="text"
                                        inputMode="tel"
                                        value={p.contact}
                                        onChange={(e) =>
                                            updatePurchaser(index, "contact", e.target.value)
                                        }
                                        placeholder="Phone"
                                        disabled={loading}
                                        className="flex-1 min-w-0 h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-900 placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/40"
                                    />
                                </div>
                            </div>
                        ))}

                        <button
                            type="button"
                            onClick={addPurchaser}
                            disabled={loading}
                            className="w-full flex items-center justify-center gap-2 h-10 rounded-xl border border-dashed border-gray-300 bg-white text-xs font-bold uppercase tracking-wide text-gray-600 transition-all hover:border-[#55C5D0]/60 hover:text-[#3E9BA4]"
                        >
                            <UserPlus className="w-3.5 h-3.5" />
                            Add another purchaser
                        </button>
                        <p className="text-[11px] font-medium text-gray-400">
                            Optional &mdash; add the buyer(s). Add more than one when a
                            property is bought jointly.
                        </p>
                    </div>

                    {/* Post-sale buyer claim — auto-sent when the buyer is known */}
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-[#55C5D0]/10 border border-[#55C5D0]/20">
                        <Users className="w-4 h-4 text-[#55C5D0] mt-0.5 shrink-0" />
                        <span className="block text-xs font-medium text-gray-500 leading-relaxed">
                            If we have the buyer&rsquo;s email (from the purchaser details
                            above or their accepted offer), they&rsquo;ll be emailed a link
                            to claim this property in their My Property Portfolio. Otherwise
                            you can send it from the listing once the sale is marked.
                        </span>
                    </div>

                    {error ? (
                        <p className="text-xs font-medium text-red-500">{error}</p>
                    ) : null}
                </div>

                <DialogFooter className="gap-3 sm:gap-0 sm:flex-row flex-col">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="flex-1 px-6 py-3 text-xs font-black uppercase tracking-widest text-gray-500 bg-gray-100 border border-transparent rounded-xl hover:bg-gray-200 transition-all font-sans"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={loading}
                        className="flex-1 px-6 py-3 text-xs font-black uppercase tracking-widest text-white rounded-xl transition-all font-sans shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 bg-[#55C5D0] hover:bg-[#4AADB7] focus:ring-[#55C5D0]"
                    >
                        {loading ? (
                            <>
                                <span className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                Processing...
                            </>
                        ) : isEdit ? (
                            "Save Changes"
                        ) : (
                            "Mark as Sold"
                        )}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default MarkSoldModal;
